import {
  ArrowLeftIcon,
  Button,
  CloseIcon,
  FolderIcon,
  Header,
  IconButton,
  Paragraph,
  TextInput,
  TrashCanIcon,
  notification,
} from "@datagame/ui";
import { Dialog } from "@headlessui/react";
import { Field, Form, Formik } from "formik";
import { object, string } from "yup";
import { Trans, useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";

import DeleteFolderImage from "../../../../assets/images/deleteFolder.svg";
import { DIRECTORY_LIST } from "../../../../graphql/queries/ProjectQueries";
import { Section } from "../../../project/ProjectView/ProjectSettings/Steps/Common";
import { Folder } from "../../../project/ProjectsView/FoldersSection/FoldersList";
import { DIRECTORY_DELETE } from "../../../../graphql/mutations/FolderMutations";
import { FieldWrapper } from "../../ui/FieldWrapper";

import styles from "./DeleteFolderModal.module.scss";

interface DeleteFolderModalProps {
  onClose: () => void;
  folder?: Folder | null;
}

export const DeleteFolderModal = ({ onClose, folder }: DeleteFolderModalProps) => {
  const { t } = useTranslation();

  const [deleteDirectory] = useMutation(DIRECTORY_DELETE, {
    refetchQueries: [{ query: DIRECTORY_LIST }],
  });

  const handleDelete = () => {
    folder &&
      deleteDirectory({
        variables: {
          directoryId: folder.id,
        },
        onError: (error) => {
          notification.danger({ title: error.message, description: "" });
        },
      });
  };

  const initialValues = {
    name: "",
  };

  const validationSchema = object({
    name: string()
      .equals([folder?.name], t("folders:deleteModal.confirmDeleteError") ?? "")
      .required(t("folders:deleteModal.confirmDeleteError") ?? ""),
  });

  return (
    <Dialog open={true} onClose={onClose}>
      <div className={styles.overlay} />
      <Dialog.Panel>
        <div className={styles.modal}>
          <div className={styles.header}>
            <IconButton size="small" onClick={onClose} className={styles.close} icon={<ArrowLeftIcon />} />

            <IconButton size="small" onClick={onClose} className={styles.close} icon={<CloseIcon />} />
          </div>
          <div className={styles.deleteImageWrapper}>
            <img src={DeleteFolderImage} alt="delete-folder"></img>
          </div>

          <Formik
            initialValues={initialValues}
            onSubmit={handleDelete}
            validationSchema={validationSchema}
            validateOnBlur={false}
            validateOnChange={false}
          >
            {({ errors }) => (
              <Form className={styles.form}>
                <Section
                  headerClassName={styles.deleteHeader}
                  title={t("folders:deleteModal.title")}
                  className={styles.section}
                >
                  <Paragraph color="secondary">
                    <Trans i18nKey={t("folders:deleteModal.description", { name: folder?.name })} />
                  </Paragraph>

                  <div>
                    <Header variant="h6" className={styles.warning}>
                      {t("folders:deleteModal.warning")}
                    </Header>
                    <Paragraph size="small" color="secondary">
                      {t("folders:deleteModal.warningDescription")}
                    </Paragraph>
                  </div>
                  <div>
                    <Paragraph weight="font-bold" className={styles.toConfirm}>
                      {t("folders:deleteModal.toConfirm")}
                    </Paragraph>
                    <FieldWrapper label="" name="name">
                      <Field
                        size="small"
                        placeholder={t("folders:deleteModal.namePlaceholder") ?? ""}
                        name="name"
                        as={TextInput}
                        icon={<FolderIcon />}
                        error={errors.name}
                      />
                    </FieldWrapper>
                  </div>
                </Section>

                <div className={styles.buttons}>
                  <Button icon={<CloseIcon />} kind="outline" iconSide="left" onClick={onClose} color="black">
                    {t("common:cancel")}
                  </Button>
                  <Button icon={<TrashCanIcon />} color="red" iconSide="left" type="submit">
                    {t("common:delete")}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};
